import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { ASSET_STATUSES, EV_CONNECTOR_TYPES } from "../masterdata/assets.js";
import { productTypeSchema, productUnitSchema } from "../masterdata/products.js";
import { evCommandSchema } from "./commands.js";
import { evChargeComponentSchema, evConnectionCloseReasonSchema, evConnectorStatusSchema, evCurrentTypeSchema, evLoadMethodSchema, evOperationModelSchema, evTariffScopeSchema, evUnknownIdTagPolicySchema, } from "./enums.js";
import { evSessionSummarySchema } from "./sessions.js";
/**
 * Şarj TANIMLARI (Prompt 19, ADR 0019): ünitelerin OCPP görünümü ve kimlik bilgisi, istasyon şarj ayarı (işletme modeli,
 * yük yönetimi), tarifeler (+ deneme / yürürlükteki tarife) ve ekran seçicileri. Ünitenin kendisi (ad, OCPP kimliği,
 * konnektörler) assets modülünündür (`stations.assets.manage`); burada yalnızca okunur. İzinler: okuma `ev.read`, yazma
 * `ev.manage`; bayrak `EV`.
 */
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
const versionSchema = z.number().int().min(0).describe("İyimser kilit sürümü (kayıt yoksa 0)");
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_OR_END = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;
const decimalInput = (integerDigits, scale, label) => z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{1,${integerDigits}}(\\.\\d{1,${scale}})?$`), `${label} en fazla ${scale} ondalıklı, negatif olmayan sayı olmalıdır`);
const kwInput = decimalInput(6, 2, "Güç (kW)");
const priceInput = decimalInput(12, 4, "Fiyat (₺)");
const PARTNER_CODE = /^[A-Z0-9][A-Z0-9_-]{1,39}$/;
// ── Üniteler ─────────────────────────────────────────────────────────────────
export const listEvChargersQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    includeUnmanaged: queryBoolean
        .optional()
        .describe("true: OCPP kimliği olmayan, şarj ağı istasyonundaki ve hizmetten çıkarılmış üniteler de listelenir (panel). " +
        "Varsayılan yalnızca bu sunucuya bağlanacak üniteler (kendi CSMS istasyonu + OCPP kimliği + hizmette) — simülatör"),
});
export const evChargerConnectorSchema = z.object({
    connectorNo: z.number().int(),
    connectorType: z.enum(EV_CONNECTOR_TYPES),
    powerKw: z.string(),
    currentType: evCurrentTypeSchema,
    assetStatus: z.enum(ASSET_STATUSES).describe("Tanımdaki durum (assets)"),
    ocppStatus: evConnectorStatusSchema
        .nullable()
        .describe("Son bilinen OCPP durumu; ünite hiç bildirmediyse null"),
    errorCode: z.string().nullable(),
    statusSince: timestampSchema.nullable(),
});
export const evChargerOcppStateSchema = z.object({
    connected: z.boolean(),
    connectedSince: timestampSchema.nullable(),
    lastSeenAt: timestampSchema.nullable(),
    lastBootAt: timestampSchema.nullable(),
    vendor: z.string().nullable(),
    model: z.string().nullable(),
    serialNo: z.string().nullable(),
    firmwareVersion: z.string().nullable(),
    firmwareStatus: z.string().nullable(),
    diagnosticsStatus: z.string().nullable(),
});
export const evChargerCredentialStateSchema = z.object({
    configured: z.boolean().describe("Platform dizininde parola özeti var mı"),
    enabled: z.boolean(),
    securityProfile: z.number().int().nullable(),
    rotatedAt: timestampSchema.nullable(),
});
export const evChargerSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    name: z.string(),
    ocppIdentity: z.string().nullable(),
    brand: z.string().nullable(),
    assetModel: z.string().nullable(),
    maxPowerKw: z.string().nullable(),
    status: z.enum(ASSET_STATUSES),
    operationModel: evOperationModelSchema,
    managed: z
        .boolean()
        .describe("Bu sunucuya bağlanır: istasyon kendi CSMS'inde, OCPP kimliği tanımlı, hizmetten çıkarılmamış"),
    credential: evChargerCredentialStateSchema,
    ocpp: evChargerOcppStateSchema,
    connectors: z.array(evChargerConnectorSchema),
});
export const evChargerListSchema = z.object({ items: z.array(evChargerSummarySchema) });
export const evConfigurationKeySchema = z.object({
    key: z.string(),
    readonly: z.boolean(),
    value: z.string().nullable(),
});
export const evConnectionIntervalSchema = z.object({
    connectedAt: timestampSchema,
    disconnectedAt: timestampSchema.nullable(),
    closeReason: evConnectionCloseReasonSchema.nullable(),
    durationSeconds: z.number().int().describe("Açık aralıkta şu ana kadar"),
});
export const evChargerDetailSchema = evChargerSummarySchema.extend({
    configuration: z.array(evConfigurationKeySchema).describe("Son GetConfiguration yanıtının anlık görüntüsü"),
    configurationReadAt: timestampSchema.nullable(),
    connections: z.array(evConnectionIntervalSchema).describe("Son 20 bağlantı aralığı (en yeni önce)"),
    commandCounts: z.record(z.string(), z.number().int()).describe("Son 30 günün komutları, durum başına adet"),
    recentCommands: z.array(evCommandSchema).describe("Son 10 komut"),
    liveSessions: z.array(evSessionSummarySchema),
});
// ── Kimlik bilgisi (OCPP güvenlik profili 1) ──────────────────────────────────
export const evChargerCredentialsSchema = z.object({
    chargerId: uuidSchema,
    ocppIdentity: z.string(),
    /** Düz parola YALNIZCA bu yanıtta döner; sunucuda argon2id özeti saklanır. Her çağrı yenisini üretir. */
    password: z.string().min(24),
    securityProfile: z.literal(1),
    ocppPath: z.string().describe("/ocpp/{ocppIdentity}"),
    ocppUrl: z
        .string()
        .describe("Ünitenin merkezi sistem (CSMS) adresi: `OCPP_PUBLIC_URL` + `/{ocppIdentity}` (ör. wss://ocpp.ornek.com/ocpp/CP-01)"),
    rotatedAt: timestampSchema,
});
export const evChargerCredentialStatusSchema = evChargerCredentialStateSchema.extend({
    chargerId: uuidSchema,
    ocppIdentity: z.string().nullable(),
});
// ── İstasyon şarj ayarı ──────────────────────────────────────────────────────
export const evStationIdParamsSchema = z.object({ stationId: uuidSchema });
export const evStationSettingsSchema = z.object({
    stationId: uuidSchema,
    companyId: uuidSchema,
    operationModel: evOperationModelSchema,
    partnerCode: z.string().nullable(),
    partnerStationRef: z.string().nullable(),
    partnerConfig: z.record(z.string(), z.string()).describe("Ortak adaptörünün gizli OLMAYAN ayarı"),
    maxGridKw: z.string().nullable(),
    loadMethod: evLoadMethodSchema,
    safetyMarginPercent: z.string(),
    minSessionPowerKw: z.string(),
    priorityFleet: z.number().int(),
    prioritySubscription: z.number().int(),
    priorityApp: z.number().int(),
    unknownIdTagPolicy: evUnknownIdTagPolicySchema,
    electricityMeterId: uuidSchema.nullable().describe("EV alt sayacı (enerji maliyeti raporu)"),
    electricityMeterName: z.string().nullable(),
    /** Kayıt yoksa varsayılanlar (`version: 0`). */
    isDefault: z.boolean(),
    version: z.number().int(),
    updatedAt: timestampSchema.nullable(),
    canEdit: z.boolean().describe("Çağıran bu istasyonda `ev.manage` taşıyor mu"),
});
/** PUT — ayar TAMAMEN yazılır (kısmi güncelleme değil; `save…` adlandırması). */
export const saveEvStationSettingsSchema = z
    .object({
    operationModel: evOperationModelSchema.default("OWN_CSMS"),
    partnerCode: z
        .string()
        .trim()
        .regex(PARTNER_CODE, "Ortak kodu büyük harf / rakam / _ - (2–40)")
        .nullable()
        .default(null),
    partnerStationRef: z.string().trim().min(1).max(100).nullable().default(null),
    partnerConfig: z
        .record(z.string().trim().min(1).max(60), z.string().max(200))
        .default({})
        .refine((value) => Object.keys(value).length <= 20, "En çok 20 ayar")
        .describe("Gizli OLMAYAN ayar (uç adı, bölge…); anahtarlar ortam değişkenindedir"),
    maxGridKw: kwInput
        .refine((value) => Number(value) > 0, "Azami güç sıfırdan büyük olmalıdır")
        .nullable()
        .default(null)
        .describe("Şebeke / trafo azami gücü (kW)"),
    loadMethod: evLoadMethodSchema.default("OFF"),
    safetyMarginPercent: decimalInput(2, 2, "Emniyet payı (%)")
        .refine((value) => Number(value) <= 50, "Emniyet payı en çok %50")
        .default("10"),
    minSessionPowerKw: kwInput.default("1.40").describe("Verilemeyen oturum askıya alınır (0 W)"),
    priorityFleet: z.number().int().min(0).max(100).default(0),
    prioritySubscription: z.number().int().min(0).max(100).default(0),
    priorityApp: z.number().int().min(0).max(100).default(0),
    unknownIdTagPolicy: evUnknownIdTagPolicySchema.default("REJECT"),
    electricityMeterId: uuidSchema.nullable().default(null),
    version: versionSchema,
})
    .superRefine((value, context) => {
    if (value.loadMethod !== "OFF" && value.maxGridKw === null)
        context.addIssue({
            code: "custom",
            path: ["maxGridKw"],
            message: "Yük yönetimi açıkken şebeke azami gücü zorunludur",
        });
    if (value.operationModel === "NETWORK_PARTNER" && value.partnerCode === null)
        context.addIssue({
            code: "custom",
            path: ["partnerCode"],
            message: "Şarj ağı işletmecisi modelinde ortak kodu zorunludur",
        });
});
// ── Tarifeler ────────────────────────────────────────────────────────────────
export const evTariffBandSchema = z.object({
    weekdays: z.array(z.number().int()),
    from: z.string(),
    to: z.string(),
    energyPrice: z.string(),
});
export const evTariffBandInputSchema = z.object({
    weekdays: z
        .array(z.number().int().min(1).max(7))
        .max(7)
        .default([])
        .refine((days) => new Set(days).size === days.length, "Aynı gün iki kez seçilemez")
        .describe("ISO hafta günleri (1 = Pazartesi … 7 = Pazar); boş = her gün"),
    from: z.string().regex(TIME, "Saat HH:MM olmalı").describe("İstanbul saati"),
    to: z.string().regex(TIME_OR_END, "Saat HH:MM ya da 24:00 olmalı").describe("İstanbul saati (hariç)"),
    energyPrice: priceInput.describe("Bu bantta kWh fiyatı (₺, KDV dahil)"),
});
const tariffFields = z.object({
    companyId: uuidSchema,
    name: z.string().trim().min(1, "Tarife adı zorunludur").max(120),
    scopeKind: evTariffScopeSchema,
    stationId: uuidSchema.nullable().default(null).describe("STATION / CHARGER (ve istasyona özgü güç sınıfı)"),
    chargerId: uuidSchema.nullable().default(null).describe("Yalnızca CHARGER"),
    currentType: evCurrentTypeSchema.nullable().default(null).describe("Yalnızca POWER_CLASS"),
    minPowerKw: kwInput.nullable().default(null).describe("Yalnızca POWER_CLASS: konnektör gücü ≥"),
    maxPowerKw: kwInput.nullable().default(null).describe("Yalnızca POWER_CLASS: konnektör gücü <"),
    validFrom: timestampSchema,
    validTo: timestampSchema.nullable().default(null),
    energyPrice: priceInput.describe("Temel kWh fiyatı (₺, KDV dahil; bant yoksa / bant dışında)"),
    minutePrice: priceInput.default("0").describe("Şarj süresi dakika ücreti (₺)"),
    idlePrice: priceInput.default("0").describe("Bekleme dakika ücreti (₺), ücretsiz süreden sonra"),
    idleGraceMinutes: z.number().int().min(0).max(1440).default(0),
    idleCap: priceInput
        .refine((value) => Number(value) > 0, "Tavan sıfırdan büyük olmalıdır")
        .nullable()
        .default(null)
        .describe("Bekleme ücreti tavanı (₺); boş = tavansız"),
    energyProductId: uuidSchema.describe("Enerji satış ürünü (tür EV_ENERGY)"),
    timeProductId: uuidSchema.nullable().default(null).describe("Süre ürünü (EV_ENERGY ya da SERVICE)"),
    idleProductId: uuidSchema.nullable().default(null).describe("Bekleme ürünü (EV_ENERGY ya da SERVICE)"),
    bands: z.array(evTariffBandInputSchema).max(50).default([]),
});
function tariffRules(value, context) {
    const issue = (path, message) => context.addIssue({ code: "custom", path: [path], message });
    const powerClassOnly = value.currentType !== null || value.minPowerKw !== null || value.maxPowerKw !== null;
    switch (value.scopeKind) {
        case "COMPANY":
            if (value.stationId)
                issue("stationId", "Şirket tarifesinde istasyon seçilmez");
            if (value.chargerId)
                issue("chargerId", "Şirket tarifesinde ünite seçilmez");
            if (powerClassOnly)
                issue("currentType", "Güç sınıfı alanları yalnızca güç sınıfı tarifesinde");
            break;
        case "STATION":
            if (!value.stationId)
                issue("stationId", "İstasyon seçilmelidir");
            if (value.chargerId)
                issue("chargerId", "İstasyon tarifesinde ünite seçilmez");
            if (powerClassOnly)
                issue("currentType", "Güç sınıfı alanları yalnızca güç sınıfı tarifesinde");
            break;
        case "CHARGER":
            if (!value.chargerId)
                issue("chargerId", "Ünite seçilmelidir");
            if (powerClassOnly)
                issue("currentType", "Güç sınıfı alanları yalnızca güç sınıfı tarifesinde");
            break;
        case "POWER_CLASS":
            if (value.chargerId)
                issue("chargerId", "Güç sınıfı tarifesinde ünite seçilmez");
            if (!powerClassOnly)
                issue("currentType", "Akım türü ya da güç aralığı seçilmelidir");
            break;
    }
    if (value.minPowerKw !== null &&
        value.maxPowerKw !== null &&
        Number(value.maxPowerKw) <= Number(value.minPowerKw))
        issue("maxPowerKw", "Üst güç alt güçten büyük olmalıdır");
    if (value.validTo !== null && Date.parse(value.validTo) <= Date.parse(value.validFrom))
        issue("validTo", "Bitiş başlangıçtan sonra olmalıdır");
    if (Number(value.minutePrice) > 0 && !value.timeProductId)
        issue("timeProductId", "Dakika ücreti için süre ürünü seçilmelidir");
    if (Number(value.idlePrice) > 0 && !value.idleProductId)
        issue("idleProductId", "Bekleme ücreti için bekleme ürünü seçilmelidir");
}
export const createEvTariffSchema = tariffFields.superRefine(tariffRules);
/** PUT — tarife TAMAMEN yazılır (bantlar dahil); şirket değişmez. Başlamış oturumlar anlık görüntü taşır, etkilenmez. */
export const saveEvTariffSchema = tariffFields
    .omit({ companyId: true })
    .extend({ version: z.number().int().positive() })
    .superRefine(tariffRules);
export const deactivateEvTariffSchema = z.object({ version: z.number().int().positive() });
export const evTariffSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    scopeKind: evTariffScopeSchema,
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    chargerId: uuidSchema.nullable(),
    chargerName: z.string().nullable(),
    currentType: evCurrentTypeSchema.nullable(),
    minPowerKw: z.string().nullable(),
    maxPowerKw: z.string().nullable(),
    validFrom: timestampSchema,
    validTo: timestampSchema.nullable(),
    energyPrice: z.string(),
    minutePrice: z.string(),
    idlePrice: z.string(),
    idleGraceMinutes: z.number().int(),
    idleCap: z.string().nullable(),
    energyProductId: uuidSchema,
    energyProductName: z.string(),
    timeProductId: uuidSchema.nullable(),
    timeProductName: z.string().nullable(),
    idleProductId: uuidSchema.nullable(),
    idleProductName: z.string().nullable(),
    bands: z.array(evTariffBandSchema),
    isActive: z.boolean(),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    canEdit: z.boolean().describe("Çağıran tarifenin kapsamında `ev.manage` taşıyor mu"),
});
export const evTariffPageSchema = pageOf(evTariffSchema);
export const listEvTariffsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional().describe("Bu istasyonu kapsayan tarifeler (şirket varsayılanı dahil)"),
    scopeKind: evTariffScopeSchema.optional(),
    includeInactive: queryBoolean.optional(),
});
const nonNegativeDecimal = (label) => z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,3})?$/, `${label} en fazla 3 ondalıklı, negatif olmayan sayı olmalıdır`);
/** Tarife deneme: verilen ünite + konnektör + başlangıç + kWh + süreler → `resolveTariff` + `priceSession`. */
export const evTariffPreviewRequestSchema = z.object({
    chargerId: uuidSchema,
    connectorNo: z.number().int().min(1).max(20),
    startedAt: timestampSchema,
    energyKwh: nonNegativeDecimal("Enerji (kWh)"),
    chargingMinutes: nonNegativeDecimal("Şarj süresi (dk)"),
    idleMinutes: nonNegativeDecimal("Bekleme (dk)").default("0"),
    coveredKwh: nonNegativeDecimal("Abonelikle karşılanan (kWh)").default("0"),
    tariffId: uuidSchema
        .nullable()
        .default(null)
        .describe("Verilirse bu tarife kullanılır (yürürlük çözülmez) — taslak tarife denemesi"),
});
export const evTariffPreviewLineSchema = z.object({
    component: evChargeComponentSchema,
    quantity: z.string(),
    unit: z.enum(["KWH", "MINUTE"]),
    unitPrice: z.string(),
    amount: moneySchema,
    productId: uuidSchema.nullable(),
});
export const evTariffPreviewSchema = z.object({
    tariff: evTariffSchema.nullable().describe("Yürürlükte tarife yoksa null (tutar 0)"),
    resolvedBy: z.enum(["RESOLVED", "GIVEN", "NONE"]),
    energyKwh: z.string(),
    coveredKwh: z.string(),
    chargingMinutes: z.string(),
    idleMinutes: z.string(),
    billableIdleMinutes: z.string(),
    lines: z.array(evTariffPreviewLineSchema),
    total: moneySchema,
    note: z
        .string()
        .describe("Deneme varsayımı: enerji şarj süresine doğrusal yayılır (saat bantları bu varsayımla bölünür)"),
});
export const evEffectiveTariffQuerySchema = z.object({
    chargerId: uuidSchema,
    connectorNo: z.coerce.number().int().min(1).max(20),
    at: timestampSchema.optional().describe("Varsayılan: şimdi"),
});
export const evEffectiveTariffSchema = z.object({
    at: timestampSchema,
    chargerId: uuidSchema,
    connectorNo: z.number().int(),
    tariff: evTariffSchema.nullable(),
    energyPriceNow: z.string().nullable().describe("`at` anındaki kWh fiyatı (bant uygulanmış)"),
});
// ── Seçiciler ────────────────────────────────────────────────────────────────
export const evPickersQuerySchema = z.object({ stationId: uuidSchema.optional() });
export const evPickersSchema = z.object({
    companies: z.array(z.object({ id: uuidSchema, name: z.string() })),
    /** ŞARJ iş birimi etkin (`station_business_units.unit_type = 'EV'`), `EV` bayrağı açık, kapsamdaki etkin istasyonlar. */
    stations: z.array(z.object({
        id: uuidSchema,
        companyId: uuidSchema,
        code: z.string(),
        name: z.string(),
        operationModel: evOperationModelSchema,
    })),
    chargers: z.array(z.object({
        id: uuidSchema,
        stationId: uuidSchema,
        name: z.string(),
        ocppIdentity: z.string().nullable(),
        maxPowerKw: z.string().nullable(),
        status: z.enum(ASSET_STATUSES),
        connectors: z.array(z.object({
            connectorNo: z.number().int(),
            connectorType: z.enum(EV_CONNECTOR_TYPES),
            powerKw: z.string(),
            currentType: evCurrentTypeSchema,
        })),
    })),
    /**
     * Tarife ürünleri: enerji için `EV_ENERGY`, süre / bekleme için `EV_ENERGY` ya da `SERVICE` (etkin). Ürün kataloğu kiracı
     * düzeyindedir; seçicide şarj istasyonu yoksa boş döner (tarife tanımlanacak şirket / istasyon yok).
     */
    products: z.array(z.object({
        id: uuidSchema,
        code: z.string(),
        name: z.string(),
        productType: productTypeSchema,
        unit: productUnitSchema,
        vatRateCode: z.string(),
    })),
    /** EV alt sayacı seçimi: istasyonların etkin ELEKTRİK sayaçları. */
    meters: z.array(z.object({
        id: uuidSchema,
        stationId: uuidSchema.nullable(),
        name: z.string(),
        businessUnitType: z.string().nullable(),
    })),
});
//# sourceMappingURL=definitions.js.map